import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { initializeAppCheck, provideAppCheck, ReCaptchaV3Provider } from '@angular/fire/app-check';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getAnalytics, provideAnalytics, ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';
import { environment } from 'src/environment';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatListModule } from '@angular/material/list';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSortModule } from '@angular/material/sort';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { REGION } from '@angular/fire/compat/functions';
import { DashboardComponent } from './dashboard/dashboard.component';
import { FooterComponent } from './footer/footer.component';
import { HeaderComponent } from './header/header.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SettingsComponent } from './settings/settings.component';
import { HomeComponent } from './home/home.component';
import { SettingsUserComponent } from './settings-user/settings-user.component';
import { SettingsNameComponent } from './settings-name/settings-name.component';
import { SettingsOwnerComponent } from './settings-owner/settings-owner.component';
import { ConfirmDialogDialog } from './confirm-dialog/confirm-dialog.component';
import { CompetitionCreateComponent } from './competition-create/competition-create.component';
import { CompetitionEditComponent } from './competition-edit/competition-edit.component';
import { StageComponent } from './stage/stage.component';
import { TeamCreateComponent } from './team-create/team-create.component';
import { TeamEditComponent } from './team-edit/team-edit.component';
import { PlayerCreateComponent } from './player-create/player-create.component';
import { StageControlPanelComponent } from './stage-control-panel/stage-control-panel.component';
import { PlayerEditComponent } from './player-edit/player-edit.component';
import { BadgeRegistryComponent } from './badge-registry/badge-registry.component';
import { BadgeMealCounterComponent } from './badge-meal-counter/badge-meal-counter.component';
import { NotesDialogComponent } from './notes-dialog/notes-dialog.component';
import { BadgeMealCounterExternalComponent } from './badge-meal-counter-external/badge-meal-counter-external.component';
import { StageScheduleComponent } from './stage-schedule/stage-schedule.component';
import { DateAdapter, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { VetoListComponent } from './veto-list/veto-list.component';
import { VetoCreateComponent } from './veto-create/veto-create.component';
import { VetoSettingsComponent } from './veto-settings/veto-settings.component';
import { VetoComponent } from './veto/veto.component';
import { RedirectDialogComponent } from './redirect-dialog/redirect-dialog.component';
import { VetoInfoDialogComponent } from './veto-info-dialog/veto-info-dialog.component';
import { RefereePanelComponent } from './referee-panel/referee-panel.component';
import { WorkspaceCreateDialogComponent } from './workspace-create-dialog/workspace-create-dialog.component';
import { SetMondayAsFirstDayOfWeek } from 'src/services/calendar-adapter';

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    DashboardComponent,
    SettingsComponent,
    HomeComponent,
    SettingsUserComponent,
    SettingsNameComponent,
    SettingsOwnerComponent,
    ConfirmDialogDialog,
    CompetitionCreateComponent,
    CompetitionEditComponent,
    StageComponent,
    StageControlPanelComponent,
    TeamCreateComponent,
    TeamEditComponent,
    PlayerCreateComponent,
    PlayerEditComponent,
    BadgeRegistryComponent,
    BadgeMealCounterComponent,
    BadgeMealCounterExternalComponent,
    NotesDialogComponent,
    StageScheduleComponent,
    VetoCreateComponent,
    VetoComponent,
    VetoListComponent,
    VetoSettingsComponent,
    RedirectDialogComponent,
    VetoInfoDialogComponent,
    RefereePanelComponent,
    WorkspaceCreateDialogComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatToolbarModule,
    MatButtonModule,
    MatCardModule,
    MatTabsModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule, 
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatDialogModule,
    MatMenuModule,
    MatSidenavModule,
    MatListModule,
    MatExpansionModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatSelectModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatNativeDateModule,
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    provideStorage(() => getStorage()),
    provideFunctions(() => getFunctions()),
    provideAnalytics(() => getAnalytics()),
    provideAppCheck(() => initializeAppCheck(getApp(), {provider: new ReCaptchaV3Provider("6LcNc1UtAAAAAF_y4G2glDf2Ye66R-jAEm0_t6in")})),
  ],
  providers: [
    ScreenTrackingService,
    UserTrackingService,
    { 
      provide: FIREBASE_OPTIONS, 
      useValue: environment.firebaseConfig 
    },
    { 
      provide: REGION, 
      useValue: 'europe-west1' 
    },
    { 
      provide: MAT_DATE_LOCALE, 
      useValue: 'de-DE' 
    },
    {
      provide: DateAdapter,
      useClass: SetMondayAsFirstDayOfWeek
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }